import {computed, Inject, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Subscription} from 'rxjs';
import {FaultyEcuBuild, FaultyEcuBuildPage} from '../models/FaultyEcuBuild';

@Injectable({providedIn: 'root'})
export class FaultyEcuBuildsService {
    readonly faultyEcuBuilds = signal<FaultyEcuBuild[]>([]);
    readonly pageNumber = signal(0);
    readonly pageSize = signal(50);
    readonly totalElements = signal(0);
    readonly totalPages = signal(0);
    readonly isLoading = signal(false);
    readonly error = signal('');
    readonly searchString = signal('');
    readonly canLoadMore = computed(() => this.pageNumber() + 1 < this.totalPages() && !this.isLoading() && !this.error());
    private request?: Subscription;
    private requestedPage = 0;
    private requestedAppend = false;

    constructor(@Inject('API_URL') private apiUrl: string, private http: HttpClient) {}

    reset(): void { this.search(''); }

    search(value: string): void {
        this.searchString.set(value.trim());
        this.pageNumber.set(0);
        this.totalElements.set(0);
        this.totalPages.set(0);
        this.faultyEcuBuilds.set([]);
        this.loadPage(0);
    }

    refresh(): void { this.search(this.searchString()); }
    retry(): void { this.loadPage(this.requestedPage, this.requestedAppend); }
    onScroll(): void { if (this.canLoadMore()) this.loadPage(this.pageNumber() + 1, true); }

    cancel(): void {
        this.request?.unsubscribe();
        this.isLoading.set(false);
    }

    private loadPage(pageNumber: number, append = false): void {
        this.cancel();
        this.requestedPage = pageNumber;
        this.requestedAppend = append;
        this.error.set('');
        this.isLoading.set(true);
        const search = this.searchString();
        const endpoint = search ? 'suggestFaultyEcuBuilds' : 'faultyEcuBuilds';
        const params: Record<string, string | number> = {pageNumber, pageSize: this.pageSize()};
        if (search) params['ecuBuild'] = search;
        this.request = this.http.get<FaultyEcuBuildPage>(`${this.apiUrl}/ecuBuildNames/${endpoint}`, {params}).subscribe({
            next: page => {
                if (!Array.isArray(page?.content)) {
                    this.error.set('Could not load faulty ECU builds. Please try again.');
                } else {
                    const rows = page.content.map(row => typeof row === 'string'
                        ? {value: row, count: null}
                        : {value: row.value, count: row.count ?? null});
                    this.faultyEcuBuilds.update(previous => [...new Map(
                        (append ? [...previous, ...rows] : rows).map(row => [row.value, row])
                    ).values()]);
                    this.pageNumber.set(pageNumber);
                    this.totalElements.set(page.totalElements);
                    this.totalPages.set(page.totalPages);
                }
                this.isLoading.set(false);
            },
            error: () => {
                this.error.set('Could not load faulty ECU builds. Please try again.');
                this.isLoading.set(false);
            }
        });
    }
}
